import { useCallback, useRef, useState } from 'react';
import { faBadgeCheck } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Snackbar } from '@sixui/ui';

import type { IFormDialogProps } from '~/components/molecules/FormDialog';
import type { IBadgesFormProps, IBadgesFormValues } from './BadgesForm';
import { FormDialog } from '~/components/molecules/FormDialog';
import { BadgesForm } from './BadgesForm';

const SNACKBAR_AUTO_HIDE_DURATION = 3000;

export type IBadgesFormDialogProps = Pick<
  IFormDialogProps,
  'opened' | 'onClose' | 'headline'
> &
  IBadgesFormProps & {
    messageOnSuccess: React.ReactNode;
  };

export const BadgesFormDialog: React.FC<IBadgesFormDialogProps> = (props) => {
  const {
    opened,
    onClose,
    onSubmit,
    headline,
    messageOnSuccess,
    submitButtonLabel,
    ...other
  } = props;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleClose = useCallback((): void => {
    setIsSubmitting(false);
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
    onClose?.();
  }, [onClose]);

  const handleSubmit = useCallback(
    (values: IBadgesFormValues): Promise<void> => {
      setIsSubmitting(true);

      return Promise.resolve(onSubmit?.(values))
        .then(() => {
          setSubmitted(true);
          handleClose();
        })
        .finally(() => setIsSubmitting(false));
    },
    [onSubmit, handleClose],
  );

  return (
    <>
      <Snackbar
        open={submitted}
        onClose={() => setSubmitted(false)}
        autoHideDuration={SNACKBAR_AUTO_HIDE_DURATION}
      >
        {messageOnSuccess}
      </Snackbar>

      <FormDialog
        opened={opened}
        onClose={handleClose}
        headline={headline}
        submitButtonLabel={submitButtonLabel}
        size="lg"
        submitting={isSubmitting}
        icon={<FontAwesomeIcon icon={faBadgeCheck} />}
      >
        {({ formId }) => (
          <BadgesForm
            id={formId}
            extended
            hideActionBar
            onSubmit={handleSubmit}
            {...other}
          />
        )}
      </FormDialog>
    </>
  );
};
